/**
 * MapNode — zoom-responsive node on the adjacency map canvas.
 *
 * Three display modes based on zoom level:
 *   - Dot   (zoom < 0.15): Colored circle, tooltip on hover
 *   - Label (0.15 ≤ zoom < 0.30): Rounded rect with name text
 *   - Card  (zoom ≥ 0.30): Name (up to 2 lines) + status dot
 *
 * Description is shown only in the NodeDetailModal, not on the card.
 *
 * Nodes scale naturally with zoom up to CAP_ZOOM (0.47). Above that,
 * inverse scaling keeps nodes at their 0.47 screen size so they don't
 * grow further. This ensures 9 nodes fit in a ring 3 sector (3×3 grid)
 * at maximum visual size.
 *
 * Card: 625×130 SVG units. Canvas: 10000 (canvasRadius 5000).
 * Ring 3 arc at mid-radius (3225): 2534 units. 3×625 = 1875, gap 659.
 * Ring 3 depth: 1050 units. 3×130 = 390, gap 660.
 */

import type { AdjacencyNode } from '../../types/adjacency';
import {
  INITIATIVE_COLORS,
  INITIATIVE_COLORS_DARK,
  STATUS_COLORS,
  STATUS_DEFAULT_COLOR,
} from '../../utils/map-colors';

// ── Props ────────────────────────────────────────────────────────────

interface MapNodeProps {
  node: AdjacencyNode;
  isSelected: boolean;
  isHovered: boolean;
  passesFilter: boolean;
  zoomLevel: number;
  onSelect: (id: string, e?: React.MouseEvent) => void;
  onDoubleClick?: (id: string) => void;
  onContextMenu?: (id: string, e: React.MouseEvent) => void;
  onDragStart?: (id: string, e: React.MouseEvent) => void;
  onHoverStart: (id: string) => void;
  onHoverEnd: () => void;
}

// ── Constants ────────────────────────────────────────────────────────

/** Zoom threshold: below this → dot mode */
const ZOOM_DOT_MAX = 0.15;
/** Zoom threshold: at or above this → card mode */
const ZOOM_CARD_MIN = 0.30;

/** Zoom level at which node visual size is capped. */
const CAP_ZOOM = 0.47;

/** Card dimensions in SVG units (10000-unit canvas space) */
const CARD_W = 625;
const CARD_H = 130;

/** Label mode dimensions */
const LABEL_W = 430;
const LABEL_H = 68;

/** Dot mode radius */
const DOT_RADIUS = 18;

/** Max characters per line of name text in card mode. */
const NAME_LINE_MAX = 29;
/** Max characters for name in label mode (single line). */
const NAME_MAX_LABEL = 28;

// ── Font sizes ──────────────────────────────────────────────────────

/** At 47% cap zoom: 32 × 0.47 = 15px on screen — clearly readable */
const FONT_NAME_CARD = 32;
/** Label mode: 27 × 0.25 = 6.75px at mid-label zoom */
const FONT_NAME_LABEL = 27;

/** Status indicator dot radius */
const STATUS_R = 10;

// ── Helpers ──────────────────────────────────────────────────────────

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max - 1) + '\u2026' : text;
}

// ── Component ────────────────────────────────────────────────────────

export function MapNode({
  node,
  isSelected,
  isHovered,
  passesFilter,
  zoomLevel,
  onSelect,
  onDoubleClick,
  onContextMenu,
  onDragStart,
  onHoverStart,
  onHoverEnd,
}: MapNodeProps) {
  const fillPrimary = INITIATIVE_COLORS[node.strategicInitiative];
  const fillDark = INITIATIVE_COLORS_DARK[node.strategicInitiative];
  const statusColor = node.status ? STATUS_COLORS[node.status] : STATUS_DEFAULT_COLOR;

  const fill = isSelected || isHovered ? fillPrimary : fillDark;
  const strokeColor = fillPrimary;
  const strokeWidth = isSelected ? 3 : isHovered ? 2.5 : 1.5;
  const strokeOpacity = isSelected || isHovered ? 0.9 : 0.5;

  // Ghosted nodes (filtered out)
  const isGhosted = !passesFilter;
  const groupOpacity = isGhosted ? 0.05 : 1;

  // Glow filter
  const filterAttr = isSelected
    ? 'url(#glow-strong)'
    : isHovered
      ? 'url(#glow)'
      : undefined;

  // Cap node visual size above CAP_ZOOM
  const nodeScale = zoomLevel > CAP_ZOOM ? CAP_ZOOM / zoomLevel : 1;

  // Determine display mode
  const mode: 'dot' | 'label' | 'card' =
    zoomLevel < ZOOM_DOT_MAX ? 'dot' :
    zoomLevel < ZOOM_CARD_MIN ? 'label' :
    'card';

  // ── Dot mode ──────────────────────────────────────────────────────

  if (mode === 'dot') {
    return (
      <g
        data-node-id={node.id}
        opacity={groupOpacity}
        onClick={(e) => { e.stopPropagation(); if (!isGhosted) onSelect(node.id, e); }}
        onDoubleClick={(e) => { e.stopPropagation(); if (!isGhosted) onDoubleClick?.(node.id); }}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (!isGhosted) onContextMenu?.(node.id, e); }}
        onMouseDown={(e) => { if (e.shiftKey && !isGhosted) { e.stopPropagation(); onDragStart?.(node.id, e); } }}
        onMouseEnter={() => { if (!isGhosted) onHoverStart(node.id); }}
        onMouseLeave={onHoverEnd}
        style={{ cursor: isGhosted ? 'default' : 'pointer', pointerEvents: isGhosted ? 'none' : 'auto' }}
        filter={filterAttr}
        transform={nodeScale < 1 ? `scale(${nodeScale})` : undefined}
      >
        <circle
          r={DOT_RADIUS}
          fill={fill}
          fillOpacity={0.6}
          stroke={strokeColor}
          strokeWidth={2}
          strokeOpacity={strokeOpacity}
        />
      </g>
    );
  }

  // ── Label / Card mode ─────────────────────────────────────────────

  const W = mode === 'card' ? CARD_W : LABEL_W;
  const H = mode === 'card' ? CARD_H : LABEL_H;

  // Card: split name into up to 2 lines with word-wrap
  // Label: single truncated line
  const nameLines: string[] = [];
  if (mode === 'card') {
    const name = node.name;
    if (name.length <= NAME_LINE_MAX) {
      nameLines.push(name);
    } else {
      const breakAt = name.lastIndexOf(' ', NAME_LINE_MAX);
      const split = breakAt > 0 ? breakAt : NAME_LINE_MAX;
      nameLines.push(name.slice(0, split));
      nameLines.push(truncate(name.slice(split).trimStart(), NAME_LINE_MAX));
    }
  }

  const truncatedName = mode === 'label' ? truncate(node.name, NAME_MAX_LABEL) : '';

  return (
    <g
      data-node-id={node.id}
      opacity={groupOpacity}
      onClick={(e) => { e.stopPropagation(); if (!isGhosted) onSelect(node.id, e); }}
      onDoubleClick={(e) => { e.stopPropagation(); if (!isGhosted) onDoubleClick?.(node.id); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (!isGhosted) onContextMenu?.(node.id, e); }}
      onMouseDown={(e) => { if (e.shiftKey && !isGhosted) { e.stopPropagation(); onDragStart?.(node.id, e); } }}
      onMouseEnter={() => { if (!isGhosted) onHoverStart(node.id); }}
      onMouseLeave={onHoverEnd}
      style={{ cursor: isGhosted ? 'default' : 'pointer', pointerEvents: isGhosted ? 'none' : 'auto' }}
      filter={filterAttr}
      transform={nodeScale < 1 ? `scale(${nodeScale})` : undefined}
    >
      {/* Glass shadow */}
      <rect
        x={-W / 2 - 2}
        y={-H / 2 - 2}
        width={W + 4}
        height={H + 4}
        rx={10}
        fill="#000"
        opacity={0.3}
        filter="url(#glass-blur)"
        style={{ pointerEvents: 'none' }}
      />

      {/* Glass backdrop */}
      <rect
        x={-W / 2}
        y={-H / 2}
        width={W}
        height={H}
        rx={8}
        fill="#0f172a"
        fillOpacity={0.5}
        style={{ pointerEvents: 'none' }}
      />

      {/* Tinted fill */}
      <rect
        x={-W / 2}
        y={-H / 2}
        width={W}
        height={H}
        rx={8}
        fill={fill}
        fillOpacity={isHovered ? 0.7 : 0.45}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
      />

      {/* Top highlight */}
      <rect
        x={-W / 2 + 8}
        y={-H / 2 + 2}
        width={W - 16}
        height={H * 0.35}
        rx={6}
        fill="url(#glass-highlight)"
        style={{ pointerEvents: 'none' }}
      />

      {/* Name text — card mode (1–2 lines) */}
      {mode === 'card' && nameLines.map((line, i) => (
        <text
          key={i}
          x={-W / 2 + 20}
          y={nameLines.length === 1 ? 0 : -18 + i * 38}
          fill="#f1f5f9"
          fontSize={FONT_NAME_CARD}
          fontWeight={700}
          dominantBaseline="central"
          style={{ pointerEvents: 'none' }}
        >
          {line}
        </text>
      ))}

      {/* Name text — label mode (single line) */}
      {mode === 'label' && (
        <text
          x={-W / 2 + 16}
          y={0}
          fill="#f1f5f9"
          fontSize={FONT_NAME_LABEL}
          fontWeight={700}
          dominantBaseline="central"
          style={{ pointerEvents: 'none' }}
        >
          {truncatedName}
        </text>
      )}

      {/* Status dot */}
      <circle
        cx={W / 2 - 20}
        cy={H / 2 - 20}
        r={STATUS_R}
        fill={statusColor}
        stroke="#0c1222"
        strokeWidth={1.5}
      />
    </g>
  );
}
